import { cloneDeep } from 'lodash-es';
import { createRequiredSportFilter, resetRequiredSportFilter } from '@/helpers/sportFilter';
import { LeagueItem } from '@/models/league';
import { createLeaguesPageHeaders } from '@/pages/system/leagues/LeaguesPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { leagueService } from '@/services/leagueService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { usePersistentFilters } from '@/stores/composables/usePersistentFilters';
import { IPageOptions } from '@/types';
import type { CustomLeagueAssociationStatus, ILeagueFilters, ILeagueUpdateResponse } from '@/types/league';
import type { DataTableHeader } from '@/types/vuetify';

function createLeagueStoreSetup(storeId: string) {
    const items = shallowRef<LeagueItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    const filters = reactive<ILeagueFilters>({
        sportId: createRequiredSportFilter(),
        leagueCustomLeagueId: {
            value: null,
            title: 'leagues.customLeague',
            allowSaveState: true,
        },
        leagueName: {
            value: null,
            title: 'leagues.name',
            allowSaveState: true,
        },
        leagueBookmakerId: {
            value: null,
            title: 'leagues.bookmaker',
            allowSaveState: true,
        },
        customLeagueAssociationStatus: {
            value: 'all' as CustomLeagueAssociationStatus,
            title: 'leagues.associationStatus',
            allowSaveState: true,
            disableClear: true,
        },
    });

    const hiddenFilters = ref<string[]>([]);
    const visibleFiltersMobile = ref<string[]>([]);
    const filterOrder = ref<string[]>(['sportId', 'leagueCustomLeagueId', 'leagueName', 'leagueBookmakerId', 'customLeagueAssociationStatus']);

    const availableFilters = computed(() => {
        const entries = Object.entries(filters).map(([key, meta]) => ({
            key,
            title: meta.title || '',
        }));

        if (filterOrder.value.length > 0) {
            entries.sort((a, b) => {
                const indexA = filterOrder.value.indexOf(a.key);
                const indexB = filterOrder.value.indexOf(b.key);
                return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
            });
        }

        return entries;
    });

    const useButtonForFiltersApply = ref(false);
    const saveFiltersState = ref(false);

    const visibleFilters = computed(() => Object.keys(filters).filter((key) => !hiddenFilters.value.includes(key)));

    const options = ref<IPageOptions>({
        page: 1,
        itemsPerPage: 100,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [{ key: 'name', order: 'asc' }],
    });

    function resetFilters() {
        resetRequiredSportFilter(filters.sportId);
        filters.leagueCustomLeagueId.value = null;
        filters.leagueName.value = null;
        filters.leagueBookmakerId.value = null;
        filters.customLeagueAssociationStatus.value = 'all';
    }

    const headers = computed<DataTableHeader[]>(() => createLeaguesPageHeaders());
    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title) && !header.hidden)
    );

    const persistentFilters = usePersistentFilters({
        filters,
        enabled: computed(() => saveFiltersState.value && storeId === 'leagues'),
    });

    const reloadFiltersFromStorage = () => {
        resetFilters();
        if (storeId === 'leagues' && saveFiltersState.value) {
            persistentFilters.loadFromStorage();
        }
    };

    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const params = {
                filters: cloneDeep(filters),
                options: {
                    sortBy: options.value.sortBy,
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['Country', 'AuditTable'],
            };

            const response = await leagueService.list(params);

            items.value = (response.data.rows || []).map((row) => new LeagueItem(row));
            total.value = response.data.total ?? 0;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    function mergeMutationResponse(response: { data: ILeagueUpdateResponse }) {
        if (response.data.success === false) return;
        const row = response.data.row;
        if (!row) return;

        const idx = items.value.findIndex((i) => i.id === row.id);
        if (idx !== -1) {
            const next = [...items.value];
            next[idx] = new LeagueItem(row);
            items.value = next;
            triggerRef(items);
        }
    }

    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        loading,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        options,
        loadData,
        updateOptions,
        clearData,
        mergeMutationResponse,
    };
}

export function useLeagueStore(storeId: string) {
    const useStore = defineStore(storeId, () => createLeagueStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'leagues'),
    });
    return useStore();
}

export type LeagueStoreSetup = ReturnType<typeof useLeagueStore>;
